import React from "react";

import { ModifierSetDto, ModifierSetTypeDto } from "services/modifierSets/restModels";
import { ModifierSetRequestDto } from "services/modifierSets/restRequests";
import {
    Button,
    FieldGrid,
    LabeledField,
    LanguageSegmentedControl,
    Modal,
    RadioGroup,
    RadioOption,
    Stack,
    TextInput,
    ToggleSwitch,
} from "shared/ui";
import { digitsOnly } from "shared/lib/formInput";
import { CurrencyFormatter } from "shared/lib/currencyFormatter";

import { ModifierDialog } from "features/ModifierSets/components/ModifierDialog";
import { ModifierSetExternalBinding } from "features/ModifierSets/components/ModifierSetExternalBinding";
import { ModifierSetModifiersList } from "features/ModifierSets/components/ModifierSetModifiersList";
import {
    MODIFIER_SET_TYPE_DESCRIPTIONS,
    MODIFIER_SET_TYPE_LABELS,
    NO_IIKO_SET_TYPE,
} from "features/ModifierSets/constants";
import { useModifierSetForm } from "features/ModifierSets/hooks/useModifierSetForm";
import { ExternalProductOptionsContext } from "features/ModifierSets/types";

import { LangRow } from "./styles";

const SET_TYPE_OPTIONS: RadioOption<ModifierSetTypeDto>[] = [
    ModifierSetTypeDto.ChooseOne,
    ModifierSetTypeDto.ChooseMany,
].map((type) => ({
    value: type,
    label: MODIFIER_SET_TYPE_LABELS[type],
    description: MODIFIER_SET_TYPE_DESCRIPTIONS[type],
}));

interface ModifierSetDialogProps {
    open: boolean;
    modifierSet: ModifierSetDto | null;
    locale: string;
    availableLanguages: string[];
    currencyFormatter: CurrencyFormatter;
    // Заведение подключено к внешнему меню — без этого блок интеграции не нужен.
    externalIntegrationEnabled?: boolean;
    // Есть, только когда набор редактируют из позиции с подключённой интеграцией.
    externalProductOptionsContext?: ExternalProductOptionsContext | null;
    saving: boolean;
    onClose: () => void;
    onSubmit: (values: ModifierSetRequestDto) => Promise<boolean>;
}

export const ModifierSetDialog = ({
    open,
    modifierSet,
    locale,
    availableLanguages,
    currencyFormatter,
    externalIntegrationEnabled,
    externalProductOptionsContext,
    saving,
    onClose,
    onSubmit,
}: ModifierSetDialogProps) => {
    const form = useModifierSetForm({
        open,
        modifierSet,
        locale,
        saving,
        externalIntegrationEnabled,
        externalProductOptionsContext,
    });

    const handleSubmit = () => {
        if (!form.canSubmit) return;

        onSubmit(form.buildRequest());
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            maxWidth="md"
            title={modifierSet ? "Редактирование набора" : "Новый набор модификаторов"}
            actions={
                <>
                    <Button variant="outlined" color="neutral" onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!form.canSubmit}
                        onClick={handleSubmit}
                    >
                        Сохранить
                    </Button>
                </>
            }
        >
            <Stack>
                {availableLanguages.length > 1 && (
                    <LangRow>
                        <LanguageSegmentedControl
                            languages={availableLanguages}
                            activeLang={form.activeLang}
                            primaryLang={locale}
                            onChange={form.setActiveLang}
                            isMissing={(lang) => !form.name[lang]}
                        />
                    </LangRow>
                )}

                <LabeledField label="Название набора">
                    <TextInput
                        value={form.name[form.activeLang]}
                        onChange={(value) =>
                            form.setName({ ...form.name, [form.activeLang]: value })
                        }
                        placeholder="Например, «Размер» или «Добавки»"
                    />
                </LabeledField>

                <LabeledField label="Сколько вариантов выбирает гость">
                    <RadioGroup
                        value={form.type}
                        options={SET_TYPE_OPTIONS}
                        onChange={(value: string) => form.setType(value as ModifierSetTypeDto)}
                    />
                </LabeledField>

                {form.type === ModifierSetTypeDto.ChooseOne ? (
                    <ToggleSwitch
                        checked={form.isRequired}
                        onChange={form.setIsRequired}
                        label="Выбор обязателен"
                        hint="Без выбранного варианта позицию нельзя положить в корзину"
                    />
                ) : (
                    <FieldGrid $min={180}>
                        <LabeledField label="Не меньше">
                            <TextInput
                                value={form.minCount}
                                onChange={(value) => form.setMinCount(digitsOnly(value, 2))}
                                placeholder="0"
                            />
                        </LabeledField>
                        <LabeledField
                            label="Не больше"
                            hint={form.countsValid ? undefined : "Меньше минимального количества"}
                        >
                            <TextInput
                                value={form.maxCount}
                                onChange={(value) => form.setMaxCount(digitsOnly(value, 2))}
                                placeholder="0"
                            />
                        </LabeledField>
                    </FieldGrid>
                )}

                <ModifierSetModifiersList
                    modifiers={form.modifiers}
                    locale={locale}
                    currencyFormatter={currencyFormatter}
                    onAdd={() => form.openModifierDialog(null)}
                    onEdit={form.openModifierDialog}
                    onRemove={form.removeModifier}
                />

                {form.showExternalBinding && (
                    <ModifierSetExternalBinding
                        iikoType={form.iikoType}
                        onIikoTypeChange={form.changeIikoType}
                        isCommonCompoundModifier={form.isCommonCompoundModifier}
                        onCommonCompoundModifierChange={form.setIsCommonCompoundModifier}
                        externalId={form.externalId}
                        onExternalIdChange={form.setExternalId}
                        groupOptions={form.groupOptions}
                        groupOptionsLoading={form.externalOptionsLoading}
                        locale={locale}
                    />
                )}
            </Stack>

            <ModifierDialog
                open={form.isModifierDialogOpen}
                modifier={form.editedModifier}
                locale={locale}
                availableLanguages={availableLanguages}
                currencyFormatter={currencyFormatter}
                showExternalBinding={form.showExternalBinding}
                externalBindingEnabled={form.iikoType !== NO_IIKO_SET_TYPE}
                externalOptions={form.modifierExternalOptions}
                externalOptionsLoading={form.externalOptionsLoading}
                onClose={form.closeModifierDialog}
                onSubmit={form.submitModifier}
            />
        </Modal>
    );
};
